import CopyDesignDialog from '@components/Customization/ReportDesigns/CopyDesignDialog';
import NewDesignDialog from '@components/Customization/ReportDesigns/NewDesignDialog';
import ReportDesignRow from '@components/Customization/ReportDesigns/ReportDesignRow';
import { useDesignOwners } from '@components/Customization/ReportDesigns/useDesignOwners';
import DeleteConfirmationDialog from '@components/Shared/Dialog/DeleteConfirmationDialog';
import Header from '@components/Shared/Layout/Header';
import { ROUTE_PATHS } from '@constants/routes';
import { dropDraftsNotListed, keptDraftsOf, useUnsavedDrafts } from '@lib/customization/unsavedWork';
import { useLocalization } from '@lib/localization';
import { Box, Button, Divider, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { countPillStyles } from '@pages/CustomPoiTypes.styles';
import { barStyles, ownerHeaderStyles } from '@pages/ReportDesigns.styles';
import {
  groupByOwner, ownerLabel, useAddReportDesignMutation, useDeleteReportDesignMutation, useGetReportDesignsQuery,
  type CreateReportDesignInput, type ReportDesignSummary,
} from '@store/api/reportDesignEndpoints';
import { usePermission } from '@store/hooks';
import { useEffect, useMemo, useState } from 'react';
import { BuildingIcon, PlusIcon, UserFriendsIcon } from 'react-line-awesome';
import { generatePath, useNavigate } from 'react-router-dom';

/** Which dialog is open, and on which design. */
type DialogState =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'copy'; design: ReportDesignSummary }
  | { kind: 'delete'; design: ReportDesignSummary };

/**
 * The report designs the reader can open, under their owners, with what can be done to a
 * design as a whole: starting one, copying and deleting it. A row opens the design in the
 * editor, which is where everything inside it is changed, its name included: this page
 * writes no design.
 *
 * The page is for authors. Someone who can only read designs meets them where a report is
 * downloaded, and here would find every action disabled.
 */
const ReportDesignsPage = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { userId } = usePermission();

  const { data: designs, isError, isFetching, startedTimeStamp, refetch } = useGetReportDesignsQuery();
  const { owners, ready: ownersReady } = useDesignOwners();

  const [addReportDesign, { isLoading: isCreating }] = useAddReportDesignMutation();
  const [deleteReportDesign] = useDeleteReportDesignMutation();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const groups = useMemo(() => groupByOwner(designs ?? []), [designs]);

  // A list that could not be read is not an empty one: saying "no designs yet" would send
  // an author looking for designs that are still there. A re-read that fails leaves what
  // was read on screen.
  const isLoadFailed = isError && !designs;

  // The designs the reader left unsaved changes to in the editor. The drafts are kept in the
  // tab's memory only, so the list is where their author sees that they are still there.
  const unsaved = useUnsavedDrafts('report-design', userId);

  // A draft of a design the list no longer holds has no row to carry that mark and no editor
  // left to discard it in: somebody else deleted the design, its team is gone, or the reader
  // was taken out of it. It is given up by a list that was asked for after the draft was
  // last kept (`dropDraftsNotListed`). The time at hand is the list's own only while its
  // last read stands: a read that is on its way again, or one that failed, has put its own
  // in its place, over the list from before.
  const listAskedAt = isFetching || isError ? undefined : startedTimeStamp;

  useEffect(() => {
    if (!designs || listAskedAt === undefined) return;

    dropDraftsNotListed('report-design', userId, designs.map(({ _id }) => _id), listAskedAt);
  }, [userId, designs, listAskedAt]);

  const close = () => setDialog({ kind: 'none' });
  const open = (id: string) => navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_REPORT_DESIGN, { designId: id }));

  /** A copy joins the list, where it was asked for. */
  const handleCopy = async (input: CreateReportDesignInput) => {
    const result = await addReportDesign(input);

    if (!result.error) close();
  };

  /** A new design is a preset until it has been worked on, so it opens in the editor at once. */
  const handleCreate = async (input: CreateReportDesignInput) => {
    const result = await addReportDesign(input);

    if (result.error || !result.data) return;

    close();
    open(result.data.id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Box sx={{ width: '100%' }}>
        <Box sx={barStyles}>
          <Typography color="text.secondary" sx={{ minWidth: 0 }}>{t('ReportDesign_Subtitle')}</Typography>
          {owners.length > 0 && (
            <Button
              variant="contained"
              startIcon={<PlusIcon />}
              onClick={() => setDialog({ kind: 'new' })}
              // Not before the list has been read: a new design can start as a copy of one
              // in it, and a list that failed to load would offer none.
              disabled={!ownersReady || !designs}
              sx={{ flexShrink: 0 }}
            >
              {t('ReportDesign_New')}
            </Button>
          )}
        </Box>

        {groups.length === 0 && (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            {/* No list yet and no failure: it is on its way, a retry after a failure included. */}
            {!designs && !isLoadFailed && <LinearProgress />}

            {isLoadFailed && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">{t('ReportDesign_ListLoadFailed')}</Typography>
                <Button size="small" variant="outlined" color="inherit" sx={{ mt: 1.5 }} onClick={() => { void refetch(); }}>
                  {t('ReportPreview_Retry')}
                </Button>
              </Box>
            )}

            {designs && (
              <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                {t('ReportDesign_Empty')}
              </Typography>
            )}
          </Paper>
        )}

        <Stack spacing={2}>
          {groups.map((group) => (
            <Paper key={group.teamId ?? 'organization'} variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack direction="row" sx={ownerHeaderStyles}>
                {group.teamId === null ? <BuildingIcon /> : <UserFriendsIcon />}
                <Typography sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>
                  {ownerLabel(group)}
                </Typography>
                <Box component="span" sx={countPillStyles}>
                  {t('ReportDesign_Count', { count: group.designs.length })}
                </Box>
              </Stack>

              {group.designs.map((design) => (
                <Box key={design._id}>
                  <Divider />
                  <ReportDesignRow
                    design={design}
                    unsaved={unsaved.has(design._id)}
                    canCopy={owners.length > 0}
                    copyReady={ownersReady}
                    onOpen={() => open(design._id)}
                    onCopy={() => setDialog({ kind: 'copy', design })}
                    onDelete={() => setDialog({ kind: 'delete', design })}
                  />
                </Box>
              ))}
            </Paper>
          ))}
        </Stack>
      </Box>

      <NewDesignDialog
        open={dialog.kind === 'new'}
        owners={owners}
        designs={designs ?? []}
        saving={isCreating}
        onClose={close}
        onCreate={handleCreate}
      />

      <CopyDesignDialog
        design={dialog.kind === 'copy' ? dialog.design : null}
        owners={owners}
        saving={isCreating}
        onClose={close}
        onCreate={handleCopy}
      />

      <DeleteConfirmationDialog
        open={dialog.kind === 'delete'}
        warningSubtitle={dialog.kind === 'delete' ? t('ReportDesign_ConfirmDelete', [dialog.design.name]) : undefined}
        onClose={close}
        onConfirm={async () => {
          if (dialog.kind !== 'delete') return;

          const result = await deleteReportDesign(dialog.design._id);

          // A draft of a design that is gone can be saved nowhere, and would still be asked
          // about before a reload.
          if (!result.error) keptDraftsOf('report-design').drop(userId, dialog.design._id);
        }}
      />
    </Box>
  );
};

export default ReportDesignsPage;
